import type { Connector } from '../shapes/Connector';
import type { Shape } from '../shapes/Shape';
import type { TextElement } from '../shapes/TextElement';

export interface DiagramSelectionGroup {
  shapes: Shape[];
  connectors: Connector[];
  textElements: TextElement[];
}

export interface DiagramSelectionInput {
  selectedIds: Set<string>;
  shapes: Shape[];
  connectors: Connector[];
  textElements: TextElement[];
}

export const filterBySelection = <T extends { id: string; selected?: boolean }>(
  items: T[],
  selectedIds: Set<string>
): T[] => items.filter((item) => selectedIds.has(item.id) || item.selected === true);

export const filterOutSelection = <T extends { id: string; selected?: boolean }>(
  items: T[],
  selectedIds: Set<string>
): T[] => items.filter((item) => !(selectedIds.has(item.id) || item.selected === true));

export const setElementSelection = <T extends { id: string; selected?: boolean }>(
  element: T,
  selectedIds: Set<string>,
  selected: boolean
): void => {
  element.selected = selected;
  if (selected) {
    selectedIds.add(element.id);
  } else {
    selectedIds.delete(element.id);
  }
};

export const clearSelectionState = (input: DiagramSelectionInput): void => {
  const { selectedIds } = input;
  for (const shape of input.shapes) {
    setElementSelection(shape, selectedIds, false);
  }
  for (const connector of input.connectors) {
    setElementSelection(connector, selectedIds, false);
  }
  for (const textElement of input.textElements) {
    setElementSelection(textElement, selectedIds, false);
  }
  selectedIds.clear();
};

export const selectAllDiagramElements = (input: DiagramSelectionInput): void => {
  const { selectedIds } = input;
  selectedIds.clear();
  for (const shape of input.shapes) {
    setElementSelection(shape, selectedIds, true);
  }
  for (const connector of input.connectors) {
    setElementSelection(connector, selectedIds, true);
  }
  for (const textElement of input.textElements) {
    setElementSelection(textElement, selectedIds, true);
  }
};

export const getSelectedDiagramElements = (input: DiagramSelectionInput): DiagramSelectionGroup => ({
  shapes: filterBySelection(input.shapes, input.selectedIds),
  connectors: filterBySelection(input.connectors, input.selectedIds),
  textElements: filterBySelection(input.textElements, input.selectedIds),
});

export const forEachSelectedDiagramElement = (
  input: DiagramSelectionInput,
  handlers: {
    shape?: (shape: Shape) => void;
    connector?: (connector: Connector) => void;
    textElement?: (textElement: TextElement) => void;
  }
): DiagramSelectionGroup => {
  const selected = getSelectedDiagramElements(input);
  if (handlers.shape) {
    for (const shape of selected.shapes) {
      handlers.shape(shape);
    }
  }
  if (handlers.connector) {
    for (const connector of selected.connectors) {
      handlers.connector(connector);
    }
  }
  if (handlers.textElement) {
    for (const textElement of selected.textElements) {
      handlers.textElement(textElement);
    }
  }
  return selected;
};
